// imageCollage.js - Builds a simple grid collage PNG from a list of image URLs.
//
// Used for day-level sharing: rather than compositing participant/score
// overlays (a much larger rendering subsystem), a day share combines just
// the distinct game thumbnails played that day into one grid image, paired
// with a text summary of all of that day's sessions.
import { API_BASE } from '../config/api';
import { authService } from '../services/auth';
import logger from './logger';

const CELL_SIZE = 300; // logical px per grid cell, matches typical game thumbnail aspect
const MAX_COLUMNS = 3;

function loadImage(url) {
  return new Promise((resolve) => {
    if (!url) {
      resolve(null);
      return;
    }

    const isApiUrl = url.startsWith(API_BASE);
    const token = isApiUrl ? authService.getToken() : null;
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    const credentials = isApiUrl ? 'include' : 'omit';
    const timeout = setTimeout(() => resolve(null), 5000);

    // Fetch the game thumbnail with the user's auth token (for API thumbnails only)
    // and load it from a same-origin object URL. This avoids CORS/taint issues
    // that prevent the canvas from being exported when an <img> is loaded cross-origin.
    fetch(url, { headers: authHeaders, credentials })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Image fetch failed: ${res.status}`);
        }
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          clearTimeout(timeout);
          URL.revokeObjectURL(objectUrl);
          resolve(img);
        };
        img.onerror = () => {
          clearTimeout(timeout);
          URL.revokeObjectURL(objectUrl);
          resolve(null);
        };
        img.src = objectUrl;
      })
      .catch((err) => {
        clearTimeout(timeout);
        logger.warn('Failed to load collage image:', err);
        resolve(null);
      });
  });
}

/**
 * Builds a grid collage of the given image URLs and returns it as a PNG File.
 *
 * @param {string[]} imageUrls - Game thumbnail URLs (duplicates are de-duplicated).
 * @param {Object} [options]
 * @param {string} [options.filename] - Filename for the resulting File.
 * @param {string} [options.backgroundColor] - Canvas background fill.
 * @returns {Promise<File|null>} Resolves with a PNG File, or null if no images could be loaded.
 */
export async function buildImageCollage(imageUrls, {
  filename = 'day-collage.png',
  backgroundColor,
} = {}) {
  const uniqueUrls = [...new Set((imageUrls || []).filter(Boolean))];
  if (uniqueUrls.length === 0) return null;

  const loadedImages = (await Promise.all(uniqueUrls.map(loadImage))).filter(Boolean);
  if (loadedImages.length === 0) return null;

  const columns = Math.min(MAX_COLUMNS, loadedImages.length);
  const rows = Math.ceil(loadedImages.length / columns);

  const canvas = document.createElement('canvas');
  canvas.width = columns * CELL_SIZE;
  canvas.height = rows * CELL_SIZE;
  const ctx = canvas.getContext('2d');

  const resolvedBackground = backgroundColor
    || getComputedStyle(document.body).getPropertyValue('--bg-secondary')
    || '#fafbfc';
  ctx.fillStyle = resolvedBackground;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  loadedImages.forEach((img, index) => {
    const col = index % columns;
    const row = Math.floor(index / columns);
    const x = col * CELL_SIZE;
    const y = row * CELL_SIZE;

    try {
      // Cover-fit the image into its cell, cropping any excess.
      const scale = Math.max(CELL_SIZE / img.width, CELL_SIZE / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const offsetX = x + (CELL_SIZE - drawWidth) / 2;
      const offsetY = y + (CELL_SIZE - drawHeight) / 2;

      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, CELL_SIZE, CELL_SIZE);
      ctx.clip();
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
      ctx.restore();
    } catch (err) {
      logger.warn('Failed to draw collage image:', err);
    }
  });

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob ? new File([blob], filename, { type: 'image/png' }) : null);
    }, 'image/png');
  });
}
